// Carrinho, cardápio filtrado, promoções e sugestões do totem/balcão.
// Verbatim do antigo BalcaoVendas.js (só virou hook). Novo: `buscaAberta`
// (a busca é a 1ª opção da coluna de categorias e procura em tudo).
import { useState, useEffect, useMemo } from "react";
import { casaPeloCodigo } from "../../../lib/codigoBarras";
import { temPersonalizacao } from "../../../utils/cartGrouping";
import { linhasDaEtapa } from "../../../utils/precoEtapas";
import { usePizzaPopup } from "../../../components/pizza/usePizzaPopup";
import { precoPromocional } from "../lib/promocao";

export function useCarrinho({ products, allProductsForPhotos, categories, promotions }) {
  const [selectedCategory, setSelectedCategory] = useState("Todos");
  const [searchTerm, setSearchTerm] = useState("");
  const [buscaAberta, setBuscaAberta] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [popupOpen, setPopupOpen] = useState(false);
  const [cart, setCart] = useState([]);
  const [cartOpen, setCartOpen] = useState(false);
  const [addedAnim, setAddedAnim] = useState(null); // { name, key }

  // ── Cart helpers ──
  const addToCart = (product) => {
    const qty = product.quantity || 1;
    setCart((prev) => {
      // Item personalizado NUNCA agrupa. `subitems`/`etapas` entram na conta
      // porque a PIZZA guarda tamanho, sabores e borda ali — e em lugar nenhum
      // mais: ela nao seta observation/combo_type/step_selections. Sem isso,
      // uma Grande e uma Pequena do mesmo sabor viravam a MESMA linha (o `id`
      // da pizza e' o do sabor primario, nao o da combinacao) e a segunda sumia
      // com preco, tamanho e borda. Mesma regra que o PDV (Delivery.js) ja
      // aplicava corretamente.
      if (temPersonalizacao(product)) return [...prev, { ...product, quantity: qty, cartItemId: Date.now() }];
      const existing = prev.find((i) => i.id === product.id && !temPersonalizacao(i));
      if (existing) {
        const alvo = existing.cartItemId || existing.id;
        return prev.map((i) => (i.cartItemId || i.id) === alvo ? { ...i, quantity: i.quantity + qty } : i);
      }
      return [...prev, { ...product, quantity: qty, cartItemId: Date.now() }];
    });
    setAddedAnim({ name: product.name, key: Date.now() });
    setTimeout(() => setAddedAnim(null), 1800);
  };

  // Pizza tem fluxo dedicado (tamanho → sabores → borda → adicionais → finalizar)
  const pizza = usePizzaPopup({
    onAddToCart: (item) => addToCart({
      ...item,
      sale_price: item.preco || item.sale_price || 0,
      final_price: item.preco || item.sale_price || 0,
      quantity: item.quantity || 1,
    }),
    allProducts: allProductsForPhotos.length > 0 ? allProductsForPhotos : products,
    darkMode: true, // BalcaoVendas usa tema dark sempre
  });

  const removeFromCart = (cid) => setCart((p) => p.filter((i) => (i.cartItemId || i.id) !== cid));
  const updateCartQty = (cid, delta) => setCart((p) => p.map((i) => {
    if ((i.cartItemId || i.id) === cid) {
      // Item da etiqueta da balança: o peso é o da etiqueta (o servidor confere).
      if (i.etiqueta_balanca) return i;
      // Para itens divisíveis, ajusta delta de acordo com a unidade (kg/L = 0.1, un = 0.5)
      const stepDelta = i.is_divisible ? (i.sale_unit === "kg" || i.sale_unit === "L" ? 0.1 : 0.5) : 1;
      const adjustedDelta = delta > 0 ? stepDelta : -stepDelta;
      const nq = parseFloat((i.quantity + adjustedDelta).toFixed(3));
      return nq <= 0 ? null : { ...i, quantity: nq };
    }
    return i;
  }).filter(Boolean));

  const cartTotal = useMemo(() => cart.reduce((s, i) => s + (i.final_price || i.sale_price) * i.quantity, 0), [cart]);
  // Item por peso (0,426 kg) conta como 1 volume — somar os quilos dava "3.381000…".
  const cartCount = useMemo(() => cart.reduce((s, i) => s + (Number.isInteger(i.quantity) ? i.quantity : 1), 0), [cart]);

  // ── Promo helper ──
  const getProductPromo = (product) => {
    for (const promo of promotions) {
      const pp = (promo.products || []).find((p) => p.product_id === product.id);
      if (pp) {
        // Pizza por tamanho — sem valor único pra exibir aqui.
        if (pp.sizes && typeof pp.sizes === 'object' && pp.discount_value == null) return null;
        return { value: pp.discount_value, type: promo.discount_type };
      }
    }
    return null;
  };
  // Produto que entra direto no carrinho, sem o popup (leitor, sugestão): o
  // preço da promoção vai em `final_price`, como o popup faz. Etiqueta da
  // balança NÃO passa por aqui — vale o valor impresso (decisão do dono).
  const comPromocao = (product) => {
    const preco = precoPromocional(product.sale_price || 0, getProductPromo(product));
    return preco != null && preco !== product.sale_price ? { ...product, final_price: preco } : product;
  };

  // ── Filtered products ──
  const filteredProducts = useMemo(() => {
    let fp = products;
    // Busca aberta (1ª opção da coluna) procura em TODAS as categorias.
    if (selectedCategory && !buscaAberta) fp = fp.filter((p) => p.category === selectedCategory);
    if (searchTerm.trim()) {
      const t = searchTerm.toLowerCase();
      // Código de barras digitado (parte ou inteiro, ou a etiqueta da balança) também acha o produto.
      const peloCodigo = casaPeloCodigo(t, products);
      fp = fp.filter((p) => p.name.toLowerCase().includes(t) || (p.description && p.description.toLowerCase().includes(t)) || peloCodigo(p));
    }
    return fp;
  }, [products, selectedCategory, searchTerm, buscaAberta]);

  const categoryList = useMemo(() => {
    const productCategories = new Set(products.map((p) => p.category).filter(Boolean));
    // Use API categories order, filtered to only those that have products
    const ordered = categories.filter((c) => productCategories.has(c.name)).map((c) => c.name);
    // Add any product categories not in API list (fallback)
    productCategories.forEach((cat) => { if (!ordered.includes(cat)) ordered.push(cat); });
    return ordered;
  }, [products, categories]);

  // Auto-select first category when loaded
  useEffect(() => {
    if (categoryList.length > 0 && !categoryList.includes(selectedCategory)) {
      setSelectedCategory(categoryList[0]);
    }
  }, [categoryList]);

  // ── Steps summary for order submission ──
  const getItemStepsSummary = (item) => {
    if (!item.step_selections || !item.order_steps) return [];
    const relevant = item.order_steps.filter((s) => (item.combo_type === "simples" ? !s.combo_only : true));
    const summary = [];
    relevant.forEach((step, i) => {
      const sel = item.step_selections[i] || [];
      if (sel.length > 0) {
        const names = sel.map((id) => { const si = step.items?.find((x) => x.product_id === id); return si?.product_name || ""; }).filter(Boolean);
        if (names.length > 0) summary.push({ etapa: step.name, itens: names });
      }
    });
    return summary;
  };

  const getItemSubitems = (item) => {
    if (!item.step_selections || !item.order_steps) return [];
    const relevant = item.order_steps.filter((s) => (item.combo_type === "simples" ? !s.combo_only : true));
    const subitems = [];
    relevant.forEach((step, i) => {
      const sel = item.step_selections[i] || [];
      if (sel.length > 0 && step.items) {
        const daEtapa = [];
        sel.forEach((itemId) => {
          const si = step.items.find((x) => x.product_id === itemId);
          if (si) {
            // Respeitar sync_prices: se ativo (default), usar sale_price do produto; se não, usar price_override
            const preco = step.sync_prices !== false
              ? Number(si.sale_price ?? si.price_override ?? 0)
              : (si.price_override === 0 ? 0 : Number(si.price_override ?? si.sale_price ?? 0));
            daEtapa.push({ product_id: si.product_id, nome: si.product_name, preco, step_name: step.name });
          }
        });
        // Soma, Máximo ou Subtração: o valor de cada linha segue o tipo da etapa.
        const precos = linhasDaEtapa(step, daEtapa.map((s) => ({ preco: s.preco, qtd: 1 })));
        daEtapa.forEach((s, k) => subitems.push({ ...s, preco: precos[k] }));
      }
    });
    return subitems;
  };

  // ── Suggested products logic ──
  const getSuggestedProducts = useMemo(() => {
    if (cart.length === 0 || products.length === 0) return [];
    const cartIds = new Set(cart.map(i => i.id));
    const available = products.filter(p => p.available !== false && !cartIds.has(p.id));
    if (available.length === 0) return [];

    // Detect what's in the cart by category name
    const cartCategories = new Set(cart.map(i => (i.category || "").toLowerCase()));
    const cartNames = cart.map(i => (i.name || "").toLowerCase()).join(" ");

    const beverageKeywords = ["bebida", "drink", "suco", "refrigerante", "água", "agua", "cerveja", "shake", "milkshake", "café", "cafe", "chá", "cha"];
    const sideKeywords = ["acompanhamento", "porção", "porcao", "porções", "batata", "frita", "onion", "nugget"];
    const dessertKeywords = ["sobremesa", "doce", "sorvete", "açaí", "acai", "brownie", "pudim", "torta"];

    const matchesAny = (text, keywords) => keywords.some(k => text.includes(k));

    const hasBeverage = cart.some(i => {
      const cat = (i.category || "").toLowerCase();
      const name = (i.name || "").toLowerCase();
      return matchesAny(cat, beverageKeywords) || matchesAny(name, beverageKeywords);
    });
    const hasSide = cart.some(i => {
      const cat = (i.category || "").toLowerCase();
      const name = (i.name || "").toLowerCase();
      return matchesAny(cat, sideKeywords) || matchesAny(name, sideKeywords);
    });
    const hasDessert = cart.some(i => {
      const cat = (i.category || "").toLowerCase();
      const name = (i.name || "").toLowerCase();
      return matchesAny(cat, dessertKeywords) || matchesAny(name, dessertKeywords);
    });

    const suggestions = [];

    // Suggest beverages if missing
    if (!hasBeverage) {
      const beverages = available.filter(p => {
        const cat = (p.category || "").toLowerCase();
        const name = (p.name || "").toLowerCase();
        return matchesAny(cat, beverageKeywords) || matchesAny(name, beverageKeywords);
      });
      suggestions.push(...beverages.slice(0, 3));
    }

    // Suggest sides if missing
    if (!hasSide) {
      const sides = available.filter(p => {
        const cat = (p.category || "").toLowerCase();
        const name = (p.name || "").toLowerCase();
        return matchesAny(cat, sideKeywords) || matchesAny(name, sideKeywords);
      });
      suggestions.push(...sides.slice(0, 2));
    }

    // Suggest desserts if missing
    if (!hasDessert) {
      const desserts = available.filter(p => {
        const cat = (p.category || "").toLowerCase();
        const name = (p.name || "").toLowerCase();
        return matchesAny(cat, dessertKeywords) || matchesAny(name, dessertKeywords);
      });
      suggestions.push(...desserts.slice(0, 2));
    }

    // If still low on suggestions, add some popular items
    if (suggestions.length < 3) {
      const remaining = available.filter(p => !suggestions.some(s => s.id === p.id));
      suggestions.push(...remaining.slice(0, 4 - suggestions.length));
    }

    // Deduplicate and limit
    const seen = new Set();
    return suggestions.filter(p => {
      if (seen.has(p.id)) return false;
      seen.add(p.id);
      return true;
    }).slice(0, 6);
  }, [cart, products]);

  const addSuggestionToCart = (product) => {
    if (pizza.tryOpenPizza(product)) return;
    if (product.order_steps?.length > 0 || product.product_type === "combo") {
      setSelectedProduct(product);
      setPopupOpen(true);
    } else {
      addToCart({ ...comPromocao(product), quantity: 1 });
    }
  };

  // Abrir produto: pizza tem fluxo próprio; o resto abre o popup.
  const abrirProduto = (p) => { if (pizza.tryOpenPizza(p)) return; setSelectedProduct(p); setPopupOpen(true); };

  const limpar = () => {
    setCart([]); setCartOpen(false); setSelectedCategory(""); setSearchTerm(""); setBuscaAberta(false);
    setSelectedProduct(null); setPopupOpen(false);
  };

  return {
    cart, setCart, cartOpen, setCartOpen, addedAnim, selectedCategory, setSelectedCategory, searchTerm, setSearchTerm,
    buscaAberta, setBuscaAberta, selectedProduct, setSelectedProduct, popupOpen, setPopupOpen, addToCart, pizza,
    removeFromCart, updateCartQty, cartTotal, cartCount, getProductPromo, comPromocao, filteredProducts, categoryList,
    getItemStepsSummary, getItemSubitems, getSuggestedProducts, addSuggestionToCart, abrirProduto, limpar,
  };
}
